import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import SearchBar from '../datatable/SearchBar';
import RelationDataTable from '../datatable/RelationDataTable';
import Thumbnail from '../thumbnail/Thumbnail';
import { COLUMN_STYLE, getColumnStyle } from '../datatable/util';

import { defaultRejectHandler, stringifyErrorMessage } from '../../utils/utility';
import { searchSolarEnergyByText } from '../../redux/renewable_energy/solar_energy_actions';

import {
  addSolarEnergyToBuilding,
  editSolarEnergyInBuilding,
  listSolarEnergyInBuilding,
  removeSolarEnergyFromBuilding,
} from '../../redux/projects/project_solar_energy_actions';
import { getProject } from '../../redux/projects/projects_actions';


export class SolarEnergyDataTable extends React.Component {
  constructor(props) {
    super(props);
    const { t } = props;

    this.search = {
      pageSize: this.DEFAULT_PAGE_SIZE,
      page: null,
      sorted: null,
      filtered: null,
    };

    const textColumn = getColumnStyle(COLUMN_STYLE.text);
    const coeColumn = getColumnStyle(COLUMN_STYLE.coefficient);
    const decimalColumn = getColumnStyle(COLUMN_STYLE.decimal);

    this.columns = [
      {
        Header: '',
        accessor: 'photovoltaic.thumbnail',
        Cell: row => <Thumbnail img={row.value} />,
        ...getColumnStyle(COLUMN_STYLE.thumbnail),
      },
      {
        Header: () => <b>{t('name')}</b>,
        accessor: 'photovoltaic.name',
        ...textColumn,
        entity: {
          list: (text, page) => searchSolarEnergyByText(text, page)
            .then(response => response.data.map(({name, id}) => ({label: name, value: String(id)}))),
          create: null,
        },
      },
      {
        Header: () => <b>{t('efficiency')} (%)</b>,
        accessor: 'photovoltaic.efficiency',
        ...coeColumn,
      },
      {
        Header: () => <b>{`${t('area')} (${t('sq m')})`}</b>,
        accessor: 'photovoltaic.area',
        ...coeColumn,
      },
      {
        Header: () => <b>{t('azimuth angle')}</b>,
        accessor: 'azimuth_angle',
        ...decimalColumn,
        attr: {
          type: 'number',
          isRequired: true,
          props: { min: 0, max: 359 },
        },
      },
      {
        Header: () => <b>{t('inclination')}</b>,
        accessor: 'inclination',
        ...decimalColumn,
        attr: {
          type: 'number',
          isRequired: true,
          props: { min: 0, max: 90 },
        },
      },
      {
        Header: () => <b>{t('quantity')}</b>,
        accessor: 'quantity',
        width: 80,
        ...decimalColumn,
        attr: {
          type: 'number',
          isRequired: true,
          props: { min: 1 },
        },
      },
    ];

    this.state = {};
  }

  updateSortFilter(filter, state, category) {
    if (state) {
      this.search.pageSize = state.pageSize;
      this.search.page = state.page;
      this.search.sorted = state.sorted;
    }
    this.search.filtered = filter;
    this.props.listSolarEnergy(
      this.search.pageSize,
      this.search.page,
      this.search.sorted,
      this.search.filtered,
      {
        all: null,
        public: true,
        private: false,
      }[category],
    );
    this.setState({ categoryOption: category });
  }

  render() {
    const { categoryOption } = this.state;

    const {
      buildingId,
      filtered,
      filteredCount,
      isSolarEnergyLoading,
      addSolarEnergy,
      editSolarEnergy,
      removeSolarEnergy,
      isSolarEnergyAdding,
      isSolarEnergyEditing,
      isSolarEnergyRemoving,
      errorMessage,
      t,
    } = this.props;

    const relationship = {
      create: ({ entityId, quantity, azimuth_angle, inclination }) => addSolarEnergy(entityId, buildingId, quantity,azimuth_angle, inclination),
      edit: ({ id, quantity, azimuth_angle, inclination }) => editSolarEnergy(id, quantity, azimuth_angle, inclination),
      delete: id => removeSolarEnergy(id, buildingId),
      isCreating: isSolarEnergyAdding,
      isEditing: isSolarEnergyEditing,
      isDeleting: isSolarEnergyRemoving,
    };

    return (
      <div style={{ backgroundColor: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <SearchBar onSearch={text => this.updateSortFilter(text, null, this.state.categoryOption)} />
          <p style={{ textAlign: 'right', marginRight: '20px', marginTop: '30px' }}>{t('azimuth_help_msg')}</p>
        </div>
        <RelationDataTable
          columns={this.columns}
          data={this.props.relationships}
          errorMessage={stringifyErrorMessage(errorMessage)}
          relationship={relationship}
          relationshipNameAccessor={item => item.photovoltaic.name}
          onActionDelete={item => this.setState({ isShowDeleteModal: true, selectedItem: item.original })}
          filteredCount={filteredCount}
          isLoading={isSolarEnergyLoading}
          onFetchData={state => this.updateSortFilter(filtered, state, categoryOption)}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  relationships: state.projectRE.solarEnergy.orderedIds.map(id => state.projectRE.solarEnergy.objects[id]),
  filteredCount: state.projectRE.solarEnergy.filteredCount,
  isSolarEnergyLoading: state.projectRE.solarEnergy.isLoading,
  isSolarEnergyAdding: state.projectRE.solarEnergy.isCreating,
  isSolarEnergyEditing: state.projectRE.solarEnergy.isEditing,
  isSolarEnergyRemoving: state.projectRE.solarEnergy.isDeleting,
  errorMessage: state.projectRE.solarEnergy.errorMessage,
});

const mapDispatchToProps = (dispatch, props) => ({
  listSolarEnergy: (pageSize, page, sorted, filtered, isPublic) =>
    defaultRejectHandler(dispatch(listSolarEnergyInBuilding(pageSize, page, sorted, filtered, isPublic, props.buildingId))),
  addSolarEnergy: (solarEnergyId, buildingId, quantity, azimuth, inclination) =>
    defaultRejectHandler(dispatch(addSolarEnergyToBuilding(solarEnergyId, buildingId, quantity, azimuth, inclination))
      .then(() => dispatch(getProject(props.projectId)))),
  editSolarEnergy: (buildingSolarEnergyId, quantity, azimuth, inclination) =>
    defaultRejectHandler(dispatch(editSolarEnergyInBuilding(buildingSolarEnergyId, quantity, azimuth, inclination))
      .then(() => dispatch(getProject(props.projectId)))),
  removeSolarEnergy: (buildingSolarEnergyId, buildingId) =>
    defaultRejectHandler(dispatch(removeSolarEnergyFromBuilding(buildingSolarEnergyId, buildingId))
      .then(() => dispatch(getProject(props.projectId)))),
});

export default translate('translations', { withRef: true })(
  connect(
    mapStateToProps,
    mapDispatchToProps,
    null,
    { withRef: true },
  )(SolarEnergyDataTable),
);



// WEBPACK FOOTER //
// ./src/components/project_detail/SolarEnergyDataTable.js